import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { waLink } from "@/content/site";
import { WhatsAppIcon } from "@/components/apex/primitives";

const DISMISS_KEY = "apex_wa_bubble_dismissed";

const FloatingWhatsApp = () => {
  const { t, isRtl } = useLang();
  const [showBubble, setShowBubble] = useState(false);

  useEffect(() => {
    if (window.sessionStorage.getItem(DISMISS_KEY)) return;
    const timer = setTimeout(() => setShowBubble(true), 7000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setShowBubble(false);
    window.sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const button = (
    <a
      href={waLink(t.float.defaultMsg)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.float.aria}
      data-testid="floating-whatsapp-button"
      className="ap-ping relative grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[var(--wa)] text-white shadow-[0_16px_40px_-12px_rgba(37,211,102,0.7)] transition-transform duration-500 hover:scale-110"
    >
      <WhatsAppIcon className="h-7 w-7" />
    </a>
  );

  return (
    <div
      data-testid="floating-whatsapp"
      className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] z-[60] flex items-end gap-3"
      style={
        isRtl
          ? { left: "max(1.5rem, env(safe-area-inset-left))" }
          : { right: "max(1.5rem, env(safe-area-inset-right))" }
      }
    >
      {isRtl && button}

      <AnimatePresence>
        {showBubble && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="floating-whatsapp-bubble"
            className="relative mb-1 hidden max-w-[15rem] rounded-2xl bg-white px-5 py-4 pe-9 text-[13px] font-medium leading-snug text-[var(--text)] shadow-[0_20px_50px_-20px_rgba(21,21,21,0.35)] sm:block"
          >
            {t.float.bubble}
            <button
              type="button"
              onClick={dismiss}
              aria-label={t.float.dismiss}
              data-testid="floating-whatsapp-dismiss"
              className="absolute end-2 top-2 grid h-6 w-6 place-items-center rounded-full text-[var(--muted)] transition-colors hover:bg-[var(--cream-2)] hover:text-[var(--text)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {!isRtl && button}
    </div>
  );
};

export default FloatingWhatsApp;
